
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { indexedDBService } from '@/services/offline/indexeddb';
import type { TechniqueOffline, PositionOffline } from '@/services/offline/indexeddb/schema';

// Use offline types directly
type DatabaseTechnique = TechniqueOffline;
type DatabasePosition = PositionOffline;

export const TechniquesTable = () => {
  const [techniques, setTechniques] = useState<DatabaseTechnique[]>([]);
  const [positions, setPositions] = useState<DatabasePosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await indexedDBService.initialize();

      // Fetch from cache first (offline-first approach)
      const [cachedTechniques, cachedPositions] = await Promise.all([
        indexedDBService.techniques.getAll(),
        indexedDBService.positions.getAll()
      ]);

      // Sort techniques by name
      const sortedTechniques = cachedTechniques.sort((a, b) => a.name.localeCompare(b.name));
      const sortedPositions = cachedPositions.sort((a, b) => a.name.localeCompare(b.name));

      setTechniques(sortedTechniques);
      setPositions(sortedPositions);
    } catch (error) {
      toast.error('Failed to load techniques and positions from cache');
    } finally {
      setLoading(false);
    }
  };

  const filteredTechniques = techniques.filter(technique =>
    technique.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (technique.category && technique.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (technique.difficulty && technique.difficulty.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getPositionName = (position_id: string | null) => {
    if (!position_id) return 'Unknown Position';
    const position = positions.find(p => p.id === position_id);
    return position?.name || 'Unknown Position';
  };

  const getDifficultyColor = (difficulty: string | null) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-orange-100 text-orange-800';
      case 'expert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2">Loading techniques...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Technique Library Management
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Technique
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search techniques..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          {filteredTechniques.map((technique) => (
            <div key={technique.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h3 className="font-medium">{technique.name}</h3>
                  {Boolean(technique.is_admin_created) && (
                    <Badge variant="outline" className="text-xs">
                      Admin Created
                    </Badge>
                  )}
                  {technique.difficulty && (
                    <Badge className={getDifficultyColor(technique.difficulty)}>
                      {technique.difficulty}
                    </Badge>
                  )}
                  {technique.category && (
                    <Badge variant="secondary">{technique.category}</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {technique.description || 'No description available'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Position: {getPositionName(technique.position_id)}
                </p>

              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredTechniques.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? 'No techniques found matching your search.' : 'No techniques available.'}
            </p>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Library Stats:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700">Total Techniques:</span>
              <span className="font-medium ml-1">{techniques.length}</span>
            </div>
            <div>
              <span className="text-blue-700">Admin Created:</span>
              <span className="font-medium ml-1">
                {techniques.filter(t => Boolean(t.is_admin_created)).length}
              </span>
            </div>
            <div>
              <span className="text-blue-700">Categories:</span>
              <span className="font-medium ml-1">
                {[...new Set(techniques.map(t => t.category).filter(Boolean))].length}
              </span>
            </div>
            <div>
              <span className="text-blue-700">Positions:</span>
              <span className="font-medium ml-1">{positions.length}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
